import type { ThemePalette } from '@shared-core/features/config'
import { selectedUserThemePalette } from '@shared-core/features/settings'
import { useAppSelector } from '@shared-core/store'
import { ThemeProvider } from '@shared-ui/theme'
import { getTelegramWebApp, isTMA } from '@web/libs/tg/platform'
import { applyCssThemeVariables } from '@web/themes/apply-css-theme-variables'
import { resolveSystemPalette, resolveWebColorPalette } from '@web/themes/resolve-web-color-palette'
import { type PropsWithChildren, useLayoutEffect, useState } from 'react'

// «Системная» палитра-источник для темы `system`: в Telegram Mini App берём тему Телеграма
// (WebApp.colorScheme), иначе — системную тему ОС (prefers-color-scheme). В TMA prefers-color-scheme
// в вебвью ненадёжен, поэтому явно следуем за colorScheme.
function resolveInitialSystemPalette(): ThemePalette {
  if (isTMA()) {
    const scheme = getTelegramWebApp()?.colorScheme

    if (scheme === 'dark' || scheme === 'light') return scheme
  }

  return resolveSystemPalette()
}

export function SharedUiThemeProvider({ children }: PropsWithChildren) {
  const userThemePalette = useAppSelector(selectedUserThemePalette)
  const [systemPalette, setSystemPalette] = useState(resolveInitialSystemPalette)

  useLayoutEffect(() => {
    // TMA: следуем теме Телеграма и её изменениям (событие themeChanged).
    if (isTMA()) {
      const webApp = getTelegramWebApp()

      if (webApp) {
        const sync = () => setSystemPalette(webApp.colorScheme === 'dark' ? 'dark' : 'light')

        sync()
        webApp.onEvent('themeChanged', sync)
        return () => webApp.offEvent?.('themeChanged', sync)
      }
    }

    // Обычный web: следуем системной теме ОС.
    const media = window.matchMedia('(prefers-color-scheme: dark)')
    const onChange = (event: MediaQueryListEvent) => {
      setSystemPalette(event.matches ? 'dark' : 'light')
    }

    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])

  const palette = resolveWebColorPalette(userThemePalette, systemPalette)

  useLayoutEffect(() => {
    applyCssThemeVariables(palette)
  }, [palette])

  return <ThemeProvider palette={palette}>{children}</ThemeProvider>
}
